/**
 * An administrator standing inside one family's portal.
 *
 * "View as family" used to be its own page — the old combined My Family, kept
 * alive after parents stopped seeing it — so an admin checking on a family saw
 * a screen no parent has, wrapped in the admin sidebar. This mounts the real
 * family pages instead: the same dashboard, household, enrollment, invoices and
 * student pages a parent gets, with the family named rather than inferred from
 * who is signed in.
 *
 * The sidebar becomes the family's for as long as you are here (Principle 9:
 * see what they see), with a way back to their record. Nothing here is
 * actionable; the school acts on a family from the admin side.
 */
import { useEffect, useMemo } from 'react';
import { Outlet, useOutletContext, useParams } from 'react-router-dom';
import type { AppLayoutContext } from '~/types';
import { useContextNav, FAMILY_SECTIONS } from '~/components/Sidebar/contextNav';
import { ViewAsFamilyProvider } from '~/hooks/useViewAsFamily';
import { ViewAsFamilyBanner } from '~/components/ViewAsFamilyBanner';
import { useMyFamilyQuery } from '../MyFamily/useMyFamilyQuery';

export default function ViewAsFamily() {
  const outletContext = useOutletContext<AppLayoutContext>();
  const { school, roleContext } = outletContext;
  const { familyId } = useParams();
  const selectedYear = roleContext?.selectedSchoolYear;
  const { setContextNav } = useContextNav();

  const { data } = useMyFamilyQuery(school?.id, selectedYear?.id, familyId);
  const family = data?.myFamily;

  const basePath = `/app/${school?.slug}/view-as-family/${familyId}`;

  const viewAs = useMemo(
    () => ({ familyId: familyId ?? null, readOnly: true, basePath }),
    [familyId, basePath]
  );

  // Memoised: this feeds the effect's dependencies, and a fresh [] on every
  // render would republish the group forever.
  const studentLinks = useMemo(
    () => (family?.children ?? []).map((child) => ({
      id: child.person.id,
      name: child.person.fullName,
      path: `${basePath}/students/${child.person.id}`,
      heading: 'Students',
    })),
    [family?.children, basePath]
  );

  useEffect(() => {
    setContextNav({
      kind: 'family',
      title: family?.name ?? 'Family',
      subtitle: 'Viewing as this family',
      basePath,
      sections: FAMILY_SECTIONS,
      links: studentLinks,
      backLink: family?.slug
        ? { path: `/app/${school?.slug}/families/${family.slug}`, label: 'Back to family record' }
        : { path: `/app/${school?.slug}/families`, label: 'Back to families' },
      exclusive: true,
    });
    return () => setContextNav(null);
  }, [setContextNav, family?.name, family?.slug, school?.slug, basePath, studentLinks]);

  return (
    <ViewAsFamilyProvider value={viewAs}>
      <div className="px-6 pt-6">
        <ViewAsFamilyBanner familyName={family?.name} />
      </div>
      <Outlet context={outletContext} />
    </ViewAsFamilyProvider>
  );
}
